import React, { useEffect, useMemo, useRef, useState } from 'react';

interface DraggableMusicProps {
  onClose: () => void;
}

interface StorageAudioFile {
  name: string;
  url: string;
  size?: number;
}

const DraggableMusic: React.FC<DraggableMusicProps> = ({ onClose }) => {
  const [position, setPosition] = useState({ x: window.innerWidth / 2 - 220, y: 120 });
  const [size, setSize] = useState({ width: 440, height: 280 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const [tracks, setTracks] = useState<StorageAudioFile[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0.7);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const [bouncePos, setBouncePos] = useState({ x: 20, y: 20 });
  const [bounceVel, setBounceVel] = useState({ vx: 0.6, vy: 0.5 });

  const selectedTrack = useMemo(() => {
    if (selectedIndex < 0 || selectedIndex >= tracks.length) return undefined;
    return tracks[selectedIndex];
  }, [selectedIndex, tracks]);

  const displayName = (raw: string) => {
    const base = raw.replace(/\.[a-zA-Z0-9]+$/, '');
    if (/whale/i.test(base)) return 'Whales';
    return base;
  };

  const getEmoji = (raw: string) => {
    const s = raw.toLowerCase();
    if (s.includes('happy')) return '😊';
    if (s === 'apt' || s.includes('apt.')) return '😎';
    if (s.includes('jazz') || s.includes('jazzy') || s.includes('bell')) return '🔔';
    if (s.includes('whale')) return '🐋';
    if (s.includes('ocean') || s.includes('wave')) return '🌊';
    if (s.includes('chill')) return '🧊';
    if (s.includes('ambient') || s.includes('ambience')) return '🌬️';
    if (s.includes('hope')) return '✨';
    if (s.includes('reflection')) return '🌅';
    return '🎵';
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setIsLoading(true);
        const { getStorage, ref, listAll, getDownloadURL, getMetadata } = await import('firebase/storage');
        const storage = getStorage();
        const folderRef = ref(storage, 'websiteAudio');
        const result = await listAll(folderRef);
        const items = await Promise.all(result.items.map(async (itemRef) => {
          const [url, meta] = await Promise.all([getDownloadURL(itemRef), getMetadata(itemRef).catch(() => undefined)]);
          return { name: itemRef.name, url, size: meta?.size } as StorageAudioFile;
        }));
        if (mounted) {
          setTracks(items);
          setSelectedIndex(items.length > 0 ? 0 : -1);
        }
      } catch (e) {
        console.error('Failed to load websiteAudio files', e);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.loop = true;
    }
    const audio = audioRef.current;
    audio.volume = volume;
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!selectedTrack) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    audio.src = selectedTrack.url;
    if (isPlaying) {
      audio.play().catch(() => setIsPlaying(false));
    }
  }, [selectedTrack?.url]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Bouncing emoji animation (DVD-style)
  useEffect(() => {
    const tick = () => {
      const el = previewRef.current;
      if (!el) { rafRef.current = requestAnimationFrame(tick); return; }
      const rect = el.getBoundingClientRect();
      const size = 24; // emoji box size
      setBouncePos(prev => {
        let nx = prev.x + bounceVel.vx;
        let ny = prev.y + bounceVel.vy;
        let nvx = bounceVel.vx;
        let nvy = bounceVel.vy;
        if (nx <= 4 || nx + size >= rect.width - 4) nvx = -nvx;
        if (ny <= 4 || ny + size >= rect.height - 4) nvy = -nvy;
        setBounceVel({ vx: nvx, vy: nvy });
        return { x: Math.max(4, Math.min(rect.width - size - 4, nx)), y: Math.max(4, Math.min(rect.height - size - 4, ny)) };
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [bounceVel.vx, bounceVel.vy]);

  const togglePlayPause = async () => {
    if (!audioRef.current || !selectedTrack) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try { await audioRef.current.play(); setIsPlaying(true); } catch {}
    }
  };

  const skipNext = async () => {
    if (tracks.length === 0) return;
    const next = (selectedIndex + 1) % tracks.length;
    await handleSelect(next);
  };

  const handleSelect = async (index: number) => {
    setSelectedIndex(index);
    const audio = audioRef.current;
    if (!audio) return;
    const newTrack = tracks[index];
    if (!newTrack) return;
    audio.src = newTrack.url;
    try { await audio.play(); setIsPlaying(true); } catch { setIsPlaying(false); }
  };

  const handleClose = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setIsPlaying(false);
    onClose();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.dm-controls') || (e.target as HTMLElement).closest('.dm-close') || (e.target as HTMLElement).closest('.dm-list')) return;
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragOffset({ x: e.clientX - size.width, y: e.clientY - size.height });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
      }
      if (isResizing) {
        setSize({ width: Math.max(380, e.clientX - dragOffset.x), height: Math.max(220, e.clientY - dragOffset.y) });
      }
    };
    const handleMouseUp = () => { setIsDragging(false); setIsResizing(false); };
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset]);

  return (
    <div
      className="draggable-music"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        border: '1px solid rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', cursor: 'move', background: 'linear-gradient(180deg, rgba(17,24,39,0.9), rgba(17,24,39,0.85))', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
          <span>🎵</span>
          <span>Walk‑in Music</span>
        </div>
        <button className="dm-close" onClick={handleClose} style={{ background: 'transparent', color: 'white', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: 10, padding: 12, height: '100%' }}>
        <div className="dm-list" style={{ overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ padding: 8 }}>Loading songs...</div>
          ) : tracks.length === 0 ? (
            <div style={{ padding: 8 }}>No songs found in websiteAudio</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {tracks.map((t, i) => (
                <li key={t.url}>
                  <button
                    onClick={() => handleSelect(i)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid ' + (i === selectedIndex ? '#4F46E5' : '#e5e7eb'),
                      background: i === selectedIndex ? 'rgba(79,70,229,0.08)' : 'white',
                      marginBottom: 6,
                      cursor: 'pointer'
                    }}
                    title={displayName(t.name)}
                  >
                    <span style={{ marginRight: 8 }}>{getEmoji(t.name)}</span>
                    {displayName(t.name)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="dm-controls" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={togglePlayPause}
              disabled={!selectedTrack}
              style={{
                width: 46,
                height: 46,
                borderRadius: '50%',
                border: 'none',
                background: selectedTrack ? '#111827' : '#9ca3af',
                color: 'white',
                cursor: selectedTrack ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              <span style={{ fontSize: 18 }}>{isPlaying ? '⏸' : '▶'}</span>
            </button>

            <button
              onClick={skipNext}
              disabled={tracks.length === 0}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '1px solid #e5e7eb',
                background: 'white',
                color: '#111827',
                cursor: tracks.length > 0 ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Next"
              title="Next"
            >
              <span style={{ fontSize: 18 }}>⏭</span>
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>Volume</span>
              <input type="range" min={0} max={1} step={0.01} value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} />
            </div>
          </div>

          <div ref={previewRef} style={{ flex: 1, border: '1px dashed #e5e7eb', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, #fafafa, #f5f5f5)', position: 'relative', overflow: 'hidden' }}>
            {selectedTrack ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{displayName(selectedTrack.name)}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Looping until changed</div>
              </div>
            ) : (
              <div style={{ color: '#6b7280' }}>Select a song to begin</div>
            )}
            {/* Bouncing emoji */}
            {selectedTrack && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: bouncePos.x,
                  top: bouncePos.y,
                  fontSize: 24,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                  pointerEvents: 'none'
                }}
              >
                {getEmoji(selectedTrack.name)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div onMouseDown={handleResizeMouseDown} style={{ position: 'absolute', right: 0, bottom: 0, width: 16, height: 16, cursor: 'nwse-resize', background: 'transparent' }} />
    </div>
  );
};

export default DraggableMusic;
